import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "@store/api/baseQueries";
import { activitiesApi } from "@store/api/activities";

export const agendaApi = createApi({
  reducerPath: "agendaApi",
  baseQuery: customBaseQuery(
    fetchBaseQuery({
      baseUrl: `${process.env.REACT_APP_ZOOMAPP_BACKEND_BASE_URL}/agenda`,
    })
  ),
  tagTypes: ["Agenda"],
  endpoints: (builder) => {
    return {
      createNewAgenda: builder.mutation({
        query: (agenda) => ({
          url: "/",
          method: "POST",
          body: agenda,
        }),
      }),
      duplicateAgenda: builder.mutation({
        query: (agendaId) => ({
          url: `/duplicate/${agendaId}`,
          method: "POST",
        }),
      }),
      fetchAgendas: builder.query({
        query: () => ({
          url: "/",
          method: "GET",
        }),
        providesTags: ["Agenda"],
      }),
      deleteAgenda: builder.mutation({
        query: (agendaId) => ({
          url: `/${agendaId}`,
          method: "DELETE",
        }),
      }),
      fetchSingleAgenda: builder.query({
        query: (id) => ({
          url: `/${id}`,
          method: "GET",
        }),
        providesTags: ["Agenda"],
      }),
      updateSingleAgenda: builder.mutation({
        query: ({ agendaId, meetingData: body }) => ({
          url: `/${agendaId}`,
          method: "PUT",
          body,
        }),
      }),
      goLiveAgenda: builder.mutation({
        query: ({ agendaId, meetingInstanceId }) => ({
          url: `/goLive/${agendaId}`,
          method: "PUT",
          body: { meetingInstanceId },
        }),
      }),
      stopLiveAgenda: builder.mutation({
        query: (agendaId) => ({
          url: `/stopLive/${agendaId}`,
          method: "PUT",
        }),
      }),
      instantStart: builder.mutation({
        query: (body) => ({
          url: "/instant-start",
          method: "POST",
          body,
        }),
      }),
      createSmartAgenda: builder.mutation({
        query: (body) => ({
          url: "/smart",
          method: "POST",
          body,
          headers: {
            generatefromai: "true",
          },
        }),
      }),
      getPastAgendasForReport: builder.query({
        query: () => ({
          url: "/reports",
          method: "GET",
        }),
      }),
      remixAgenda: builder.mutation({
        query: ({ agendaId, body }) => ({
          url: `/${agendaId}/remix`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Agenda"],
      }),
      skipReveal: builder.mutation({
        query: (agendaId) => ({
          url: `/${agendaId}/skip-reveal`,
          method: "PUT",
        }),
        invalidatesTags: ["Agenda"],
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          await queryFulfilled;
          dispatch(activitiesApi.util.invalidateTags(["Activity"]));
        },
      }),
      createDemoAgenda: builder.mutation({
        query: () => ({
          url: "/demo/create",
          method: "POST",
        }),
      }),
      startDemoSimulation: builder.mutation({
        query: ({ agendaId, meetingInstanceId }) => ({
          url: "/demo/start-simulation",
          method: "POST",
          body: { agendaId, meetingInstanceId },
        }),
      }),
      triggerDemoEmojiWave: builder.mutation({
        query: ({ meetingInstanceId, waveType }) => ({
          url: "/demo/emoji-wave",
          method: "POST",
          body: { meetingInstanceId, waveType },
        }),
      }),
      claimGuestAgenda: builder.mutation({
        query: (body) => ({
          url: "/smart/claim",
          method: "POST",
          body,
        }),
      }),
    };
  },
});

export const {
  useCreateNewAgendaMutation,
  useDuplicateAgendaMutation,
  useDeleteAgendaMutation,
  useFetchAgendasQuery,
  useFetchSingleAgendaQuery,
  useUpdateSingleAgendaMutation,
  useGoLiveAgendaMutation,
  useStopLiveAgendaMutation,
  useInstantStartMutation,
  useCreateSmartAgendaMutation,
  useGetPastAgendasForReportQuery,
  useRemixAgendaMutation,
  useSkipRevealMutation,
  useCreateDemoAgendaMutation,
  useStartDemoSimulationMutation,
  useTriggerDemoEmojiWaveMutation,
  useClaimGuestAgendaMutation,
} = agendaApi;
